import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import ExploreCard from '../components/explore/ExploreCard';
import friendService from '../services/friendService';
import { searchContents as searchCompetitionContents } from '../services/competitionService';

const mapContentToCard = (content) => {
  const fields = content.fields || {};

  let authorName = 'Unknown';
  let authorAvatar = '';

  if (content.user) {
    authorName = content.user.name || content.user.nickname || 'Unknown';
    authorAvatar = content.user.avatar || '';
  } else if (content.creator) {
    authorName = content.creator.name || content.creator.nickname || 'Unknown';
    authorAvatar = content.creator.avatar || '';
  }

  let coverImage = null;
  if (fields.cover_image) {
    coverImage = fields.cover_image.startsWith('http')
      ? fields.cover_image
      : `/api/v2/contents/${fields.cover_image}/file`;
  }

  return {
    id: content.id,
    title: fields.title || fields.prompt || '无标题',
    description: fields.body_text || fields.response || fields.description || '',
    coverImage,
    author: { name: authorName, avatar: authorAvatar },
    stats: {
      likes: content.interactions?.like_count || 0,
      is_liked: content.is_liked,
    },
    tags: fields.tags || [],
  };
};

const TAB_PROPOSAL = 'proposal';
const TAB_POST = 'post';

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchKeyword = (params.get('search') || '').trim();
  const tab = params.get('tab') || TAB_PROPOSAL;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const requestRef = useRef(null);

  useEffect(() => {
    if (!params.get('tab')) {
      const next = new URLSearchParams(location.search);
      next.set('tab', TAB_PROPOSAL);
      navigate(`${location.pathname}?${next.toString()}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate, params]);

  useEffect(() => {
    setPage(1);
    setItems([]);
    setHasMore(Boolean(searchKeyword));
  }, [searchKeyword, tab]);

  useEffect(() => {
    if (!params.get('tab')) return;
    if (!searchKeyword) return;

    const controller = new AbortController();
    if (requestRef.current) {
      requestRef.current.abort();
    }
    requestRef.current = controller;

    const run = async () => {
      setLoading(true);
      try {
        const exprs = [
          { field: 'status', op: 'EQ', values: ['published'] },
          { field: 'title', op: 'LIKE', values: [searchKeyword] },
        ];

        const requestParams = {
          type: 'article',
          category: tab === TAB_POST ? 'post' : 'competition',
          filter: { exprs },
          sort: { field: 'inserted_at', desc: true },
          pagination: { page, per_page: 20 },
        };

        const response =
          tab === TAB_POST
            ? await friendService.searchContents(requestParams, { signal: controller.signal })
            : await searchCompetitionContents(requestParams, { signal: controller.signal });

        const contents = response?.contents || [];
        setItems((prev) => (page === 1 ? contents : [...prev, ...contents]));

        if (response?.metadata) {
          setHasMore(response.metadata.page < response.metadata.total_pages);
        } else {
          setHasMore(contents.length === 20);
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (page === 1) setItems([]);
        setHasMore(false);
      } finally {
        if (requestRef.current === controller) {
          requestRef.current = null;
        }
        setLoading(false);
      }
    };

    run();
    return () => controller.abort();
  }, [page, params, searchKeyword, tab]);

  const cards = useMemo(() => items.map(mapContentToCard), [items]);

  const setTab = (nextTab) => {
    const next = new URLSearchParams(location.search);
    next.set('tab', nextTab);
    navigate(`${location.pathname}?${next.toString()}`, { replace: true });
  };

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    setPage((p) => p + 1);
  };

  return (
    <div className="flex flex-col gap-6 min-w-0">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setTab(TAB_PROPOSAL)}
          className={[
            'h-9 px-4 rounded-full text-sm font-bold transition-colors',
            tab === TAB_PROPOSAL ? 'bg-[#C1FF00] text-black' : 'bg-[#333333] text-white',
          ].join(' ')}
        >
          提案
        </button>
        <button
          type="button"
          onClick={() => setTab(TAB_POST)}
          className={[
            'h-9 px-4 rounded-full text-sm font-bold transition-colors',
            tab === TAB_POST ? 'bg-[#C1FF00] text-black' : 'bg-[#333333] text-white',
          ].join(' ')}
        >
          帖子
        </button>
      </div>

      {!searchKeyword && (
        <div className="text-gray-400 text-sm">请输入关键词开始搜索</div>
      )}

      <div className="grid grid-cols-[repeat(2,416px)] gap-6 justify-start">
        {cards.map((item) => (
          <ExploreCard
            key={item.id}
            {...item}
            layout={tab === TAB_PROPOSAL ? 'horizontal' : 'vertical'}
            onClick={() =>
              navigate(
                tab === TAB_PROPOSAL
                  ? `/explore/proposals/${item.id}`
                  : `/explore/friends/${item.id}`
              )
            }
          />
        ))}
        {!loading && searchKeyword && cards.length === 0 && (
          <div className="col-span-2 text-gray-400 text-center py-8">暂无搜索结果</div>
        )}
      </div>

      {loading && (
        <div className="text-center py-4 text-gray-600">
          <Spin size="large" />
        </div>
      )}

      {!loading && hasMore && searchKeyword && (
        <div className="flex justify-center py-2">
          <button
            type="button"
            onClick={handleLoadMore}
            className="h-9 px-6 rounded-full bg-[#333333] text-white text-sm font-medium hover:bg-[#3f3f3f] transition-colors"
          >
            加载更多
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;
